import React, { useMemo, useState } from "react";
import {
  Box,
  Flex,
  IconButton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  HStack,
} from "@chakra-ui/react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { useQueries } from "react-query";
import { useAuth } from "../../../../context";
import { ReportsApi } from "../../../../api";
import { REPORTING_YEARS } from "../../suppliersMock";
import LocalSpinner from "../../../../components/LocalSpinner";

const CURRENCY_SYMBOL = { GBP: "£", EUR: "€", USD: "$" };
const fmtMoney = (amount, currency) =>
  `${CURRENCY_SYMBOL[currency] || ""}${Number(amount).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;

// Spend per year from GET /suppliers/reports/engagement/spends/total/by-location
// Response: { [country]: [{ supplierName, totalAmount, currency, ... }] }
function SupplierSpend({ supplier }) {
  const { callApi } = useAuth();
  const [expanded, setExpanded] = useState({});
  const toggle = (year) => setExpanded((prev) => ({ ...prev, [year]: !prev[year] }));

  const queries = useQueries(
    REPORTING_YEARS.map((year) => ({
      queryKey: ["supplierSpendByLocation", year, supplier.supplierId],
      queryFn: () => callApi(ReportsApi.getSuppliersSpendsByLocation, {
        year,
        categoryId: supplier.categoryId,
        supplierId: supplier.supplierId,
      }),
      enabled: !!supplier.supplierId,
      cacheTime: 60,
    }))
  );

  const isFetching = queries.some((q) => q.isFetching);

  // Aggregate spend data per year
  const yearlySpend = useMemo(() => {
    return REPORTING_YEARS.map((year, idx) => {
      const data = queries[idx]?.data;
      if (!data || Object.keys(data).length === 0) return { year, total: null, byCountry: [] };
      const byCountry = Object.entries(data).map(([country, rows]) => ({
        country,
        amount: rows.reduce((acc, r) => acc + Number(r.totalAmount ?? 0), 0),
        currency: rows[0]?.currency ?? "GBP",
      })).sort((a, b) => b.amount - a.amount);
      const total = byCountry.reduce((acc, c) => acc + c.amount, 0);
      const currency = byCountry[0]?.currency ?? "GBP";
      return { year, total, currency, byCountry };
    }).filter((row) => row.total !== null);
  }, [queries]);

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} gap={3} flexWrap="wrap">
        <Text fontSize="22px" fontWeight="700" color="darkTeal">Spend data</Text>
      </Flex>

      {isFetching && yearlySpend.length === 0 ? (
        <Box mt={4}><LocalSpinner /></Box>
      ) : (
        <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
          <Table size="sm" variant="unstyled">
            <Thead>
              <Tr bg="gray.50">
                <Th py={3} px={4} width="32px" />
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Year</Th>
                <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Total spend</Th>
              </Tr>
            </Thead>
            <Tbody>
              {yearlySpend.length === 0 ? (
                <Tr>
                  <Td colSpan={3} py={6} px={4} textAlign="center">
                    <Text fontSize="14px" color="gray.400">No spend data recorded.</Text>
                  </Td>
                </Tr>
              ) : [...yearlySpend].reverse().map((entry, idx) => {
                const canExpand = entry.byCountry.length > 1;
                const isOpen = !!expanded[entry.year];
                return (
                  <React.Fragment key={entry.year}>
                    <Tr
                      borderTop={idx === 0 ? "none" : "1px solid"}
                      borderColor="gray.100"
                      _hover={{ bg: "gray.50" }}
                      cursor={canExpand ? "pointer" : "default"}
                      onClick={canExpand ? () => toggle(entry.year) : undefined}
                    >
                      <Td py={3} pl={4} pr={1} width="32px">
                        {canExpand && (
                          <IconButton
                            size="xs"
                            variant="ghost"
                            aria-label={isOpen ? "Collapse" : "Expand"}
                            icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                            onClick={(e) => { e.stopPropagation(); toggle(entry.year); }}
                          />
                        )}
                      </Td>
                      <Td py={3} px={4} fontSize="14px" color="darkTeal" fontWeight="600">{entry.year}</Td>
                      <Td py={3} px={4} fontSize="14px" color="gray.700" textAlign="right">
                        {fmtMoney(entry.total, entry.currency)}
                      </Td>
                    </Tr>
                    {isOpen && canExpand && (
                      <>
                        <Tr bg="gray.50">
                          <Td py={2} px={4} width="32px" />
                          <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Country</Td>
                          <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Spend</Td>
                        </Tr>
                        {entry.byCountry.map((c) => (
                          <Tr key={c.country} bg="gray.50">
                            <Td py={2} px={4} width="32px" />
                            <Td py={2} px={4} fontSize="13px" color="gray.700">
                              <HStack spacing={2}>
                                <Box w="8px" h="8px" bg="limeShade.400" borderRadius="sm" />
                                <Text>{c.country}</Text>
                              </HStack>
                            </Td>
                            <Td py={2} px={4} fontSize="13px" color="gray.700" textAlign="right">
                              {fmtMoney(c.amount, c.currency)}
                            </Td>
                          </Tr>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default SupplierSpend;
